import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiEnvelopeBold,
    PiPaperclipBold,
    PiXBold,
    PiCheckCircleBold,
    PiWarningCircleBold,
    PiSpinnerBold,
    PiCaretDownBold,
} from 'react-icons/pi';
import api from '@v2/lib/api';

type BrokerOption = {
    id: number;
    name: string;
    email: string | null;
    email_secondary: string | null;
    contact_person: string | null;
};

type AttachmentInfo = {
    name: string;
    available: boolean;
    source: string;
    label?: string;
};

type PreviewData = {
    to: string | null;
    cc: string | null;
    broker_id: number | null;
    broker_name: string | null;
    subject: string;
    body: string;
    attachments: AttachmentInfo[];
    has_broker: boolean;
    has_email: boolean;
    brokers: BrokerOption[];
    needs_selection: boolean;
};

type Props = {
    shipmentId: number;
    open: boolean;
    onClose: () => void;
};

function ModalContent({ shipmentId, onClose }: Omit<Props, 'open'>) {
    const [to, setTo]           = useState('');
    const [cc, setCc]           = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody]       = useState('');
    const [ready, setReady]     = useState(false);

    const preview = useQuery<PreviewData>({
        queryKey: ['customs-email-preview', shipmentId],
        queryFn: async () => {
            const { data } = await api.get<{ data: PreviewData }>(
                `/shipments/${shipmentId}/customs-email/preview`
            );
            return data.data;
        },
        staleTime: 0,
        refetchOnMount: 'always',
    });

    // Preenche os campos ao receber os dados
    useEffect(() => {
        if (preview.data && !ready) {
            setTo(preview.data.to ?? '');
            setCc(preview.data.cc ?? '');
            setSubject(preview.data.subject ?? '');
            setBody(preview.data.body ?? '');
            setReady(true);
        }
    }, [preview.data, ready]);

    // Quando o usuário seleciona outro despachante no dropdown
    const handleBrokerSelect = (brokerId: string) => {
        const broker = preview.data?.brokers.find((b) => String(b.id) === brokerId);
        if (!broker) return;
        setTo(broker.email ?? '');
        setCc(broker.email_secondary ?? '');
        const contactName = broker.contact_person
            ? broker.contact_person.split(' ')[0]
            : 'despachante';
        // Atualiza apenas o greeting do corpo, preservando o restante
        setBody((prev) => {
            const lines = prev.split('\n');
            lines[0] = `Bom dia ${contactName},`;
            return lines.join('\n');
        });
    };

    const sendMutation = useMutation({
        mutationFn: async () => {
            const { data } = await api.post<{ message: string }>(
                `/shipments/${shipmentId}/customs-email/send`,
                { to, cc: cc || undefined, subject, body }
            );
            return data;
        },
        onSuccess: (data) => {
            toast.success(data.message);
            onClose();
        },
        onError: () => {
            toast.error('Erro ao enviar e-mail. Verifique os dados e tente novamente.');
        },
    });

    const brokers     = preview.data?.brokers ?? [];
    const attachments = preview.data?.attachments ?? [];
    const canSend     = !!to && !!subject && !!body && !sendMutation.isPending;
    const currentBrokerId = brokers.find((b) => b.email === to)?.id;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

            <div className="relative z-10 w-full max-w-2xl rounded-2xl bg-white shadow-2xl dark:bg-gray-900">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-900/30">
                            <PiEnvelopeBold className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div>
                            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                                Enviar e-mail ao Despachante
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Tarefa: Gerar tradução de documentação
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
                    >
                        <PiXBold className="h-4 w-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="max-h-[70vh] overflow-y-auto px-6 py-5 space-y-4">
                    {preview.isLoading && !ready ? (
                        <div className="flex items-center justify-center py-12">
                            <PiSpinnerBold className="h-8 w-8 animate-spin text-amber-500" />
                            <span className="ml-3 text-sm text-gray-500">Carregando dados do despachante...</span>
                        </div>
                    ) : (
                        <>
                            {/* Seletor de despachante — só aparece quando há múltiplos */}
                            {brokers.length > 1 && (
                                <div>
                                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                        Despachante
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={currentBrokerId ?? ''}
                                            onChange={(e) => handleBrokerSelect(e.target.value)}
                                            className="w-full appearance-none rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                                        >
                                            <option value="">Selecionar despachante...</option>
                                            {brokers.map((b) => (
                                                <option key={b.id} value={b.id}>
                                                    {b.name}{b.email ? ` — ${b.email}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <PiCaretDownBold className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                                    </div>
                                </div>
                            )}

                            {/* Banner do despachante auto-selecionado */}
                            {preview.data?.broker_name && brokers.length <= 1 && (
                                <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2.5 text-sm text-green-800 dark:border-green-700 dark:bg-green-900/20 dark:text-green-300">
                                    <PiCheckCircleBold className="h-4 w-4 shrink-0" />
                                    <span>Despachante: <strong>{preview.data.broker_name}</strong></span>
                                </div>
                            )}

                            {/* Aviso quando não há despachante e há múltiplos para escolher */}
                            {!preview.data?.broker_name && brokers.length === 0 && (
                                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                                    <PiWarningCircleBold className="mt-0.5 h-4 w-4 shrink-0" />
                                    <span>
                                        Nenhum despachante ativo cadastrado. Cadastre um em{' '}
                                        <strong>Configurações → Despachantes</strong>.
                                    </span>
                                </div>
                            )}

                            {/* Para */}
                            <div>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Para *
                                </label>
                                <input
                                    type="email"
                                    value={to}
                                    onChange={(e) => setTo(e.target.value)}
                                    placeholder="email@despachante.com.br"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:ring-amber-900/30"
                                />
                            </div>

                            {/* CC */}
                            <div>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    CC
                                </label>
                                <input
                                    type="email"
                                    value={cc}
                                    onChange={(e) => setCc(e.target.value)}
                                    placeholder="copia@empresa.com.br (opcional)"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:ring-amber-900/30"
                                />
                            </div>

                            {/* Assunto */}
                            <div>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Assunto *
                                </label>
                                <input
                                    type="text"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:ring-amber-900/30"
                                />
                            </div>

                            {/* Corpo */}
                            <div>
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Mensagem *
                                </label>
                                <textarea
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    rows={6}
                                    className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:ring-amber-900/30"
                                />
                            </div>

                            {/* Lista de anexos */}
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <PiPaperclipBold className="h-4 w-4 text-gray-400" />
                                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                        Anexos ({attachments.length})
                                    </span>
                                </div>
                                <ul className="space-y-1.5 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
                                    {attachments.length === 0 ? (
                                        <li className="text-xs text-amber-600 dark:text-amber-400">
                                            Nenhum documento encontrado nas tarefas 29 e 30.
                                        </li>
                                    ) : (
                                        attachments.map((att, i) => (
                                            <li key={i} className="flex items-center gap-2 text-xs">
                                                <PiCheckCircleBold className="h-3.5 w-3.5 shrink-0 text-green-500" />
                                                <span className="text-gray-700 dark:text-gray-300">
                                                    {att.label ?? att.name}
                                                </span>
                                                {att.source === 'generated' && (
                                                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                                        gerado
                                                    </span>
                                                )}
                                            </li>
                                        ))
                                    )}
                                </ul>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        disabled={!canSend}
                        onClick={() => sendMutation.mutate()}
                        className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {sendMutation.isPending ? (
                            <PiSpinnerBold className="h-4 w-4 animate-spin" />
                        ) : (
                            <PiEnvelopeBold className="h-4 w-4" />
                        )}
                        {sendMutation.isPending ? 'Enviando...' : 'Enviar e-mail'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function SendCustomsEmailModal({ shipmentId, open, onClose }: Props) {
    if (!open) return null;
    return <ModalContent shipmentId={shipmentId} onClose={onClose} />;
}
